'use client'

/**
 * Run 24 - Share buttons.
 *
 * Four actions: Twitter/X, LinkedIn, Copy Link. Each fires a
 * fire-and-forget `POST /api/v1/share` so server-side analytics can
 * observe who's sharing what. Copy Link falls back to a selection
 * hack if `navigator.clipboard` isn't available.
 */

import { useCallback, useState } from 'react'
import { Copy, Check } from 'lucide-react'
import type { ShareEntityType, SharePlatform } from '@/types/growth.types'

interface ShareButtonsProps {
  url: string
  title: string
  description?: string
  entityType: ShareEntityType
  entityId: string
  className?: string
}

async function recordShare(
  entityType: ShareEntityType,
  entityId: string,
  platform: SharePlatform,
): Promise<void> {
  try {
    await fetch('/api/v1/share', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entity_type: entityType, entity_id: entityId, platform }),
      keepalive: true,
    })
  } catch {
    // Analytics is never blocking - silently swallow.
  }
}

function twitterIntent(url: string, title: string): string {
  const params = new URLSearchParams({ url, text: title })
  return `https://twitter.com/intent/tweet?${params.toString()}`
}

function linkedinIntent(url: string): string {
  const params = new URLSearchParams({ url })
  return `https://www.linkedin.com/sharing/share-offsite/?${params.toString()}`
}

export function ShareButtons({
  url,
  title,
  description,
  entityType,
  entityId,
  className,
}: ShareButtonsProps) {
  const [copied, setCopied] = useState(false)

  const openShare = useCallback(
    (platform: 'twitter' | 'linkedin', href: string) => {
      void recordShare(entityType, entityId, platform)
      window.open(href, '_blank', 'noopener,noreferrer,width=700,height=600')
    },
    [entityType, entityId],
  )

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // Legacy fallback - create a hidden textarea and use execCommand.
      const textarea = document.createElement('textarea')
      textarea.value = url
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
      } finally {
        document.body.removeChild(textarea)
      }
    }
    setCopied(true)
    void recordShare(entityType, entityId, 'copy_link')
    setTimeout(() => setCopied(false), 1500)
  }, [url, entityType, entityId])

  const btnClass =
    'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors'
  const btnStyle = {
    background: 'transparent',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-border-default)',
  } as const

  return (
    <div className={className} role="group" aria-label="Share">
      <button
        type="button"
        onClick={() => openShare('twitter', twitterIntent(url, title))}
        className={btnClass}
        style={btnStyle}
        aria-label={`Share ${title} on Twitter / X`}
      >
        <span aria-hidden>𝕏</span> Twitter
      </button>
      <button
        type="button"
        onClick={() => openShare('linkedin', linkedinIntent(url))}
        className={`${btnClass} ml-2`}
        style={btnStyle}
        aria-label={`Share ${title} on LinkedIn`}
      >
        <span aria-hidden>in</span> LinkedIn
      </button>
      <button
        type="button"
        onClick={copyLink}
        className={`${btnClass} ml-2`}
        style={btnStyle}
        aria-label={copied ? 'Link copied' : 'Copy link'}
      >
        {copied ? (
          <>
            <Check className="h-3 w-3" /> Copied
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" /> Copy link
          </>
        )}
      </button>
      {description ? <span className="sr-only">{description}</span> : null}
    </div>
  )
}
